/** Presentation helpers. Times are always rendered in the org timezone. */

export function formatDuration(ms: number, style: 'short' | 'long' = 'short'): string {
  const totalMinutes = Math.floor(Math.max(0, ms) / 60_000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (style === 'long') {
    if (hours === 0) return `${minutes} min`
    return minutes === 0 ? `${hours} hr` : `${hours} hr ${minutes} min`
  }
  return hours === 0 ? `${minutes}m` : `${hours}h ${String(minutes).padStart(2, '0')}m`
}

/** Decimal hours, the unit payroll actually wants. */
export function toDecimalHours(ms: number): string {
  return (Math.max(0, ms) / 3_600_000).toFixed(2)
}

export function formatTime(iso: string | null | undefined, timezone: string): string {
  if (!iso) return '—'
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).format(new Date(iso))
}

export function formatDate(iso: string | null | undefined, timezone: string): string {
  if (!iso) return '—'
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone, weekday: 'short', month: 'short', day: 'numeric',
  }).format(new Date(iso))
}

export function formatDateTime(iso: string | null | undefined, timezone: string): string {
  if (!iso) return '—'
  return `${formatDate(iso, timezone)} · ${formatTime(iso, timezone)}`
}

/** 'YYYY-MM-DD' for a given instant in the org timezone. */
export function localDateKey(date: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date)
  const map: Record<string, string> = {}
  for (const p of parts) map[p.type] = p.value
  return `${map.year}-${map.month}-${map.day}`
}

export function shiftDateKey(key: string, days: number): string {
  const [y, m, d] = key.split('-').map(Number)
  const date = new Date(Date.UTC(y, m - 1, d + days))
  return date.toISOString().slice(0, 10)
}

/** Minutes since local midnight for an instant, in the org timezone. */
export function localMinutes(iso: string, timezone: string): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(iso))
  const map: Record<string, string> = {}
  for (const p of parts) map[p.type] = p.value
  return Number(map.hour) * 60 + Number(map.minute)
}

export function relativeTime(iso: string): string {
  const diffMs = Date.now() - Date.parse(iso)
  const minutes = Math.round(diffMs / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

export function timeToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

export function minutesToLabel(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

const DAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

export function daysMaskLabel(mask: number): string {
  if (mask === 62) return 'Mon–Fri'
  if (mask === 127) return 'Every day'
  if (mask === 65) return 'Weekends'
  const parts: string[] = []
  for (let i = 0; i < 7; i++) if (mask & (1 << i)) parts.push(DAY_LETTERS[i])
  return parts.length ? parts.join(' ') : 'Never'
}

export function toggleDay(mask: number, day: number): number {
  return mask ^ (1 << day)
}

export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function pluralise(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`
}
